import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, Save, ChevronRight, ChevronDown, X } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const BusinessRolePermissionPanel = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { success, error: notifyError, info } = useNotification();
  
  const [positions, setPositions] = useState([]);
  const [resources, setResources] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [resourcePrivilegesMap, setResourcePrivilegesMap] = useState({});
  const [selectedPosition, setSelectedPosition] = useState('');
  const [positionInfo, setPositionInfo] = useState(null);
  const [permissionMatrix, setPermissionMatrix] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [permissionChanges, setPermissionChanges] = useState({});
  
  // 数据范围策略相关
  const [scopePolicies, setScopePolicies] = useState([]);
  const [currentScopeMappings, setCurrentScopeMappings] = useState([]);
  const [scopeChanges, setScopeChanges] = useState({});
  
  useEffect(() => {
    loadPositions();
    loadResources();
    loadPrivileges();
    loadResourcePrivileges();
  }, []);

  useEffect(() => {
    const paramId = searchParams.get('position_id');
    if (paramId && paramId !== selectedPosition) {
      setSelectedPosition(paramId);
      loadPositionInfo(paramId);
      loadPositionPermissions(paramId);
      // 加载岗位后触发 scope 数据加载（需 position code）
      loadPositionInfo(paramId).then(pos => {
        if (pos && pos.code) {
          loadScopePositionPolicies(pos.code);
        }
      });
    }
  }, [searchParams]);

  const loadPositions = async () => {
    try {
      const res = await fetch('/api/organization/positions');
      const data = await res.json();
      if (data.status === 'success') {
        setPositions(data.data);
      }
    } catch (err) {
      console.error('Error loading positions:', err);
    }
  };

  const loadResources = async () => {
    try {
      const res = await fetch('/api/organization/resources');
      const data = await res.json();
      if (data.status === 'success') {
        setResources(data.data);
      }
    } catch (err) {
      console.error('Error loading resources:', err);
    }
  };

  const loadPrivileges = async () => {
    try {
      const res = await fetch('/api/organization/privileges');
      const data = await res.json();
      if (data.status === 'success') {
        setPrivileges(data.data);
      }
    } catch (err) {
      console.error('Error loading privileges:', err);
    }
  };

  const loadResourcePrivileges = async () => {
    try {
      const res = await fetch('/api/organization/resource-privileges');
      const data = await res.json();
      if (data.status === 'success') {
        setResourcePrivilegesMap(data.data);
      }
    } catch (err) {
      console.error('Error loading resource privileges:', err);
    }
  };

  const loadPositionInfo = async (posId) => {
    try {
      const res = await fetch(`/api/organization/positions/${posId}`);
      const data = await res.json();
      if (data.status === 'success') {
        setPositionInfo(data.data);
        return data.data;
      }
    } catch (err) {
      console.error('Error loading position info:', err);
    }
    return null;
  };

  const loadPositionPermissions = async (posId) => {
    if (!posId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/organization/positions/${posId}`);
      const data = await res.json();
      if (data.status !== 'success') {
        notifyError(t('business.load_positions_failed'));
        return;
      }
      
      // Load permissions via the organization permissions API
      const permRes = await fetch(`/api/organization/permissions?position_id=${posId}`);
      const permData = await permRes.json();
      if (permData.status === 'success') {
        if (Array.isArray(permData.data)) {
          setPermissionMatrix(permData.data);
        } else {
          setPermissionMatrix([]);
        }
        setPermissionChanges({});
      }
    } catch (err) {
      console.error(err);
      notifyError(t('business.load_permissions_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 按 parent_resource_id 构建层级结构
  // 顶级资源（parent_resource_id 为 null）作为分组
  // 子资源（有 parent）嵌套在父级下面
  const groupedResources = resources.reduce((acc, resource) => {
    const parentId = resource.parent_resource_id;
    if (!parentId) {
      // 这是顶级资源，作为独立分组
      if (!acc['__top__']) acc['__top__'] = {};
      // 查找它的子资源
      const children = resources.filter(r => r.parent_resource_id === resource.resource_id);
      if (children.length > 0) {
        acc[resource.resource_name] = { parent: resource, children };
      } else {
        acc['__top__'][resource.resource_code] = resource;
      }
    }
    return acc;
  }, {});

  const hasPermission = (resourceCode, privilegeCode) => {
    const changeKey = `${resourceCode}.${privilegeCode}`;
    if (permissionChanges.hasOwnProperty(changeKey)) {
      return permissionChanges[changeKey];
    }
    
    const resourcePerms = permissionMatrix.find(r => r.resource_code === resourceCode);
    if (!resourcePerms || !resourcePerms.privileges) return false;
    
    return resourcePerms.privileges[privilegeCode]?.allowed === true;
  };

  const handlePermissionChange = (resourceCode, privilegeCode, checked) => {
    if (!selectedPosition || !positionInfo) {
      notifyError(t('org.select_position_first'));
      return;
    }
    
    const changeKey = `${resourceCode}.${privilegeCode}`;
    setPermissionChanges(prev => ({
      ...prev,
      [changeKey]: checked
    }));
  };

  const getResourcePrivileges = (resourceCode) => {
    const allowedCodes = resourcePrivilegesMap[resourceCode];
    if (!allowedCodes) return [];
    return privileges.filter(p => allowedCodes.includes(p.privilege_code));
  };

  const loadScopePolicies = async () => {
    try {
      const res = await fetch('/api/organization/scope-policies');
      const data = await res.json();
      if (data.status === 'success') {
        setScopePolicies(data.data);
      }
    } catch (err) {
      console.error('Error loading scope policies:', err);
    }
  };

  const loadScopePositionPolicies = async (posCode) => {
    if (!posCode) return;
    try {
      const res = await fetch(`/api/organization/scope-position-policies?position_code=${encodeURIComponent(posCode)}`);
      const data = await res.json();
      if (data.status === 'success') {
        setCurrentScopeMappings(data.data);
        setScopeChanges({});
      }
    } catch (err) {
      console.error('Error loading scope position policies:', err);
    }
  };

  // Load scope policies on mount
  useEffect(() => {
    loadScopePolicies();
  }, []);

  const hasScopePolicy = (policyId) => {
    const changeKey = `scope_${policyId}`;
    if (scopeChanges.hasOwnProperty(changeKey)) {
      return scopeChanges[changeKey];
    }
    return currentScopeMappings.some(m => m.policy_id == policyId && m.is_allowed == 1);
  };

  const handleScopeChange = (policyId, checked) => {
    if (!selectedPosition || !positionInfo) {
      notifyError(t('org.select_position_first'));
      return;
    }
    setScopeChanges(prev => ({
      ...prev,
      [`scope_${policyId}`]: checked
    }));
  };

  const getResourceScopes = (resourceCode) => {
    return scopePolicies.filter(p => p.resource_code === resourceCode);
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    if (!selectedPosition || !positionInfo) {
      notifyError(t('org.select_position_first'));
      return;
    }
    
    const hasPermChanges = Object.keys(permissionChanges).length > 0;
    const hasScopeChanges = Object.keys(scopeChanges).length > 0;
    
    if (!hasPermChanges && !hasScopeChanges) {
      notifyError(t('org.no_changes'));
      return;
    }
    
    setIsSubmitting(true);
    setSaving(true);
    try {
      const updates = Object.entries(permissionChanges).map(([changeKey, allowed]) => {
        const [resourceCode, privilegeCode] = changeKey.split('.');
        return {
          resource_code: resourceCode,
          privilege_code: privilegeCode,
          is_allowed: allowed ? 1 : 0
        };
      });
      
      const payload = {
        position_id: parseInt(selectedPosition),
        updates: updates
      };
      
      let allSuccess = true;
      
      // 保存权限变更
      if (hasPermChanges) {
        const permRes = await fetch('/api/organization/permissions/batch-update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const permData = await permRes.json();
        if (permData.status !== 'success') {
          notifyError(permData.message || t('org.save_failed'));
          allSuccess = false;
        }
      }
      
      // 保存数据范围变更
      if (hasScopeChanges) {
        const scopeUpdates = Object.entries(scopeChanges).map(([key, allowed]) => {
          const policyId = key.replace('scope_', '');
          return { policy_id: parseInt(policyId), is_allowed: allowed ? 1 : 0 };
        });
        
        const scopeRes = await fetch('/api/organization/scope-position-policies/batch-update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            position_code: positionInfo.code,
            updates: scopeUpdates
          })
        });
        const scopeData = await scopeRes.json();
        if (scopeData.status !== 'success') {
          notifyError(scopeData.message || t('common.scope_save_failed'), 'error');
          allSuccess = false;
        }
      }
      
      if (allSuccess) {
        await loadPositionPermissions(selectedPosition);
        await loadScopePositionPolicies(positionInfo.code);
        setPermissionChanges({});
        success(t('org.save_success'));
      }
    } catch (err) {
      console.error(err);
      notifyError(t('org.save_failed'));
    } finally {
      setSaving(false);
      setIsSubmitting(false);
    }
  };

  const toggleModule = (module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  // No position selected - show position list
  if (!selectedPosition) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="text-blue-600" />
            {t('permissions.business_role_permissions')}
          </h1>
        </div>
        
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
          <h2 className="font-medium text-slate-700 dark:text-slate-300 mb-4">
            {t("org.select_position_hint")}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {positions.map(pos => (
              <div 
                key={pos.id}
                className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                onClick={() => {
                  window.location.href = `/permissions?position_id=${pos.id}`;
                }}
              >
                <div className="font-medium text-slate-700 dark:text-slate-300">
                  {pos.name}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {pos.code} · {pos.department_name}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-full">
                    {t("org.business_roles")}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {positions.length === 0 && (
            <div className="text-center py-8">
              <p className="text-slate-500 dark:text-slate-400">
                {t("org.no_positions_yet")}
              </p>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">
                {t("org.create_dept_first")}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="text-blue-600" />
            {t("org.permission_management")}
          </h1>
          {positionInfo && (
            <div className="mt-2 flex items-center gap-3">
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {positionInfo.name} ({positionInfo.code})
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">
                {positionInfo.department_name}
              </span>
            </div>
          )}
        </div>
        <button 
          onClick={() => window.history.back()}
          className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors"
        >
          <X size={20} />
        </button>
      </div>
      
      <div className="flex gap-3 justify-end">
        <button
            onClick={handleSave}
            disabled={isSubmitting || saving || (Object.keys(permissionChanges).length === 0 && Object.keys(scopeChanges).length === 0)}
            className={`bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''} disabled:bg-slate-400 disabled:cursor-not-allowed`}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {t("common.processing")}
              </span>
            ) : (
              <><Save size={18} />{t('org.save_permissions')}</>
            )}
          </button>

      </div>

      <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-medium text-slate-700 dark:text-slate-300">
              {t("org.view_permissions")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('org.total_modules', {count: resources.length})}
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {t('org.pending_changes', {count: Object.keys(permissionChanges).length + Object.keys(scopeChanges).length})}
            </div>
          </div>
        </div>
      </div>

      {/* ===== 权限矩阵 ===== */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        {loading ? (
          <div className="px-6 py-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              {t("org.loading_permissions")}
            </p>
          </div>
        ) : (
          <>
            <div className="px-6 py-3 text-sm text-slate-500 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
              {t("org.permission_list")}
            </div>
            
            {/* 先渲染独立模块（无子资源的顶层资源） */}
            {groupedResources.__top__ && Object.values(groupedResources.__top__).map(resource => (
              <div key={resource.resource_code} className="border-b border-slate-200 dark:border-slate-700">
                <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-700">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {resource.resource_name}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {getResourcePrivileges(resource.resource_code).map(priv => {
                      const allowed = hasPermission(resource.resource_code, priv.privilege_code);
                      const changeKey = `${resource.resource_code}.${priv.privilege_code}`;
                      const isChanged = permissionChanges.hasOwnProperty(changeKey);
                      return (
                        <button
                          key={changeKey}
                          onClick={() => handlePermissionChange(resource.resource_code, priv.privilege_code, !allowed)}
                          className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 ${
                            isChanged 
                              ? 'ring-2 ring-blue-400 border-blue-400 ' + (allowed ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300')
                              : allowed 
                                ? 'bg-green-50 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                                : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700'
                          }`}
                        >
                          {priv.privilege_name}
                          {isChanged && <span className="ml-1 text-blue-500">*</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}

            {/* 再渲染有子资源的模块（父级分包，子级展开） */}
            {Object.entries(groupedResources).filter(([k]) => k !== '__top__').map(([groupName, groupData]) => (
              <div key={groupName} className="border-b border-slate-200 dark:border-slate-700 last:border-b-0">
                <div 
                  className="px-6 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer flex items-center justify-between"
                  onClick={() => toggleModule(groupName)}
                >
                  <div className="flex items-center gap-2">
                    {expandedModules[groupName] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span className="font-medium text-slate-700 dark:text-slate-300">{groupName}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded-full">{groupData.children.length}</span>
                  </div>
                </div>
                
                {expandedModules[groupName] && (
                  <div className="divide-y divide-slate-100 dark:divide-slate-700">
                    {groupData.children.map(child => (
                      <div key={child.resource_code} className="px-6 py-3 pl-12">
                        <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
                          {child.resource_name}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {getResourcePrivileges(child.resource_code).map(priv => {
                            const allowed = hasPermission(child.resource_code, priv.privilege_code);
                            const changeKey = `${child.resource_code}.${priv.privilege_code}`;
                            const isChanged = permissionChanges.hasOwnProperty(changeKey);
                            return (
                              <button
                                key={changeKey}
                                onClick={() => handlePermissionChange(child.resource_code, priv.privilege_code, !allowed)}
                                className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 ${
                                  isChanged 
                                    ? 'ring-2 ring-blue-400 border-blue-400 ' + (allowed ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300')
                                    : allowed 
                                      ? 'bg-green-50 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                                      : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700'
                                }`}
                              >
                                {priv.privilege_name}
                                {isChanged && <span className="ml-1 text-blue-500">*</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* ===== 数据范围权限 ===== */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="px-6 py-3 text-sm font-medium text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {t("org.data_scope_title")}
          </span>
          {selectedPosition && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {Object.keys(scopeChanges).length > 0 && (
                <span className="text-blue-500">{t('org.pending_changes', {count: Object.keys(scopeChanges).length})}</span>
              )}
            </span>
          )}
        </div>
        
        {!selectedPosition ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t("org.select_position_for_scope")}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {['employee', 'product', 'supplier', 'task', 'channel_products', 'channel_stores'].map(resourceCode => {
              const scopes = getResourceScopes(resourceCode);
              if (scopes.length === 0) return null;
              // Put 'all' first, then sort by policy_order
              const allScope = scopes.find(s => s.scope_type === 'all');
              const otherScopes = scopes.filter(s => s.scope_type !== 'all').sort((a, b) => a.policy_order - b.policy_order);
              const orderedScopes = allScope ? [allScope, ...otherScopes] : otherScopes;
              return (
                <div key={resourceCode} className="px-6 py-3">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                    <span className="inline-block w-1 h-4 bg-blue-500 rounded-full"></span>
                    {resourceCode.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </div>
                  <div className="flex flex-wrap gap-2 pl-3">
                    {orderedScopes.map(policy => {
                      const allowed = hasScopePolicy(policy.policy_id);
                      const changeKey = `scope_${policy.policy_id}`;
                      const isChanged = scopeChanges.hasOwnProperty(changeKey);
                      const isAll = policy.scope_type === 'all';
                      return (
                        <button
                          key={changeKey}
                          onClick={() => handleScopeChange(policy.policy_id, !allowed)}
                          className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 ${
                            isAll
                              ? allowed
                                ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 shadow-sm font-medium'
                                : 'bg-white dark:bg-slate-900 text-blue-600 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium'
                              : isChanged 
                                ? 'ring-2 ring-blue-400 border-blue-400 ' + (allowed ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300')
                                : allowed 
                                  ? 'bg-green-50 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                                  : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700'
                          }`}
                        >
                          {isAll ? (
                            <span className="flex items-center gap-1">
                              {allowed && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                              {t('org.scope_view_all')}
                            </span>
                          ) : (
                            policy.scope_type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())
                          )}
                          {isChanged && !isAll && <span className="ml-1 text-blue-500">*</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessRolePermissionPanel;
